import React, { useMemo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import ChartHelpButton from '../ChartHelpButton';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import usePersistWidgetSnapshot from '../../hooks/usePersistWidgetSnapshot';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';
import AnalyticsChartFullscreenButton from '../analytics/AnalyticsChartFullscreenButton';
import { PresetFilterStrip, PRESET_FILTER_ROW_Y, PRESET_FILTER_OPEN_PT } from '../analytics/PresetChartChrome';
import { STATUS_OPTIONS as CANONICAL_STATUS_OPTIONS } from '../home/WeatherMapControls';
import { createLeafletPinIcon } from '../map/leafletPinIcon';
import { trackGlobalProduceMapViewOncePerSession } from '../../../utils/analytics';
import { PIN_HOVER_CSS } from '../../../utils/pinHoverCss.js';
import { CARTO_LIGHT_URL, CARTO_DARK_URL } from '../../../constants/URLs';

/**
 * MapResizeHandler - Handles Leaflet map resize when container dimensions change
 *
 * Leaflet captures dimensions at mount time and doesn't automatically respond to
 * container size changes. This is problematic in react-grid-layout where widgets
 * animate into position with CSS transitions (200ms), causing the map to initialize
 * with incorrect dimensions.
 *
 * This component uses ResizeObserver to detect when the container stabilizes and
 * calls invalidateSize() to force Leaflet to recalculate its dimensions.
 */
function MapResizeHandler() {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    if (!container) return;

    // Initial invalidation after a short delay to handle CSS transition
    const initialTimer = setTimeout(() => {
      map.invalidateSize({ animate: false });
    }, 250); // Slightly longer than the 200ms CSS transition

    // ResizeObserver for ongoing container dimension changes
    const resizeObserver = new ResizeObserver((entries) => {
      // Only invalidate if we have meaningful dimensions
      const entry = entries[0];
      if (entry && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
        map.invalidateSize({ animate: false });
      }
    });

    resizeObserver.observe(container);

    // Window resize handler (covers edge cases like zoom or orientation change)
    const handleWindowResize = () => {
      map.invalidateSize({ animate: false });
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      clearTimeout(initialTimer);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [map]);

  return null;
}

// Status colors (matching backend chart_renderer.py)
const STATUS_COLORS = {
  producing: '#16A34A',       // Green - currently producing
  not_producing: '#6B7280',   // Grey - not producing
  starting_soon: '#3b82f6',   // Blue - starts next month
  ending_soon: '#f97316',     // Orange - ends next month
};

const STATUS_LABELS = {
  producing: 'Producing',
  not_producing: 'Not Producing',
  starting_soon: 'Starting Soon',
  ending_soon: 'Ending Soon',
};

// Generate Leaflet styles based on theme
const getLeafletStyles = (isDark) => `
  .leaflet-popup-content-wrapper {
    background-color: ${isDark ? '#1d1d1d' : '#ffffff'};
    color: ${isDark ? '#f0f5f2' : '#1f2937'};
    --harvest-not-producing-text: ${isDark ? '#9CA3AF' : '#6B7280'};
    border-radius: 8px;
    border: 1px solid ${isDark ? '#333' : '#e5e7eb'};
  }
  .leaflet-popup-tip {
    background-color: ${isDark ? '#1d1d1d' : '#ffffff'};
    border: 1px solid ${isDark ? '#333' : '#e5e7eb'};
  }
  .leaflet-popup-close-button {
    color: ${isDark ? '#888' : '#6b7280'} !important;
  }
  .leaflet-popup-close-button:hover {
    color: var(--bloom-green) !important;
  }
  .leaflet-container {
    background-color: ${isDark ? '#1d1d1d' : '#f3f4f6'};
  }
  .leaflet-control-attribution {
    background-color: ${isDark ? 'rgba(29, 29, 29, 0.8)' : 'rgba(255, 255, 255, 0.8)'} !important;
    color: ${isDark ? '#666' : '#6b7280'} !important;
  }
  .leaflet-control-attribution a {
    color: var(--bloom-green) !important;
  }
  .leaflet-control-zoom a {
    background-color: ${isDark ? '#282a29' : '#ffffff'} !important;
    color: ${isDark ? '#f0f5f2' : '#1f2937'} !important;
    border-color: ${isDark ? '#333' : '#d1d5db'} !important;
  }
  .leaflet-control-zoom a:hover {
    background-color: var(--bloom-green) !important;
    color: ${isDark ? '#1d1d1d' : '#ffffff'} !important;
  }
  .leaflet-interactive {
    cursor: pointer !important;
    -webkit-tap-highlight-color: rgba(21, 203, 94, 0.3) !important;
    touch-action: manipulation !important;
  }
  .leaflet-pin-icon {
    background: transparent !important;
    border: none !important;
  }
  ${PIN_HOVER_CSS}
`;

// Helper to convert to title case
const toInitCap = (str) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

// ---------- pure cascade helpers ----------
// Top-level pure functions used both by the rendering useMemos and by the
// modal handlers (which reset downstream selections to "all of new options"
// when the user changes anything upstream). Keeping them at module scope
// avoids re-creating them on every render and makes them trivially testable.
const buildCountryOptions = (markers, statuses) => {
  const countries = new Set();
  const includeAll = statuses.length === 0;
  markers.forEach((m) => {
    if (!includeAll && !statuses.includes(m.status)) return;
    if (m.data?.country) countries.add(String(m.data.country));
  });
  return [...countries].sort((a, b) => a.localeCompare(b));
};

const buildRegionOptions = (markers, statuses, countries) => {
  const regions = new Set();
  const includeAllStatuses = statuses.length === 0;
  const includeAllCountries = countries.length === 0;
  markers.forEach((m) => {
    if (!includeAllStatuses && !statuses.includes(m.status)) return;
    if (!includeAllCountries && !countries.includes(String(m.data?.country || ''))) return;
    if (m.data?.region) regions.add(String(m.data.region));
  });
  return [...regions].sort((a, b) => a.localeCompare(b));
};

const ProducingRegionsMap = ({
  data = [],
  commodity = '',
  hideTitle = false,
  hideHelpButton = false,
  presetDescription = '',
  presetSingleChartFullscreen = false,
  analyticsFullscreen = null,
}) => {
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const { isReady: analyticsPrefsReady } = useWidgetOptionsPersistence('producing-regions');
  const mapRef = useRef(null);
  // Preset view only: below lg the chips sit behind a Filters toggle, as on the
  // other preset charts. The widget dashboard keeps them always open.
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Multi-select filters. Strict semantics:
  //   empty array  -> NO markers pass for this dimension (Clear All behavior)
  //   non-empty    -> include only the selected values
  // On first visit (no Firestore snapshot) we populate all three arrays to
  // their respective `*Options` so users see the full data; from then on the
  // user explicitly opts in/out via the modal.
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const [regionModalOpen, setRegionModalOpen] = useState(false);

  // Inject custom Leaflet styles and update on theme change
  useEffect(() => {
    const styleId = 'leaflet-custom-styles-producing-regions';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = getLeafletStyles(isDark);
  }, [isDark]);

  // Convert data to markers. Use Number.isFinite so coordinates at exactly
  // 0 (equator / prime meridian) aren't dropped by a falsy check.
  const markers = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data
      .filter((item) => Number.isFinite(item.latitude) && Number.isFinite(item.longitude))
      .map((item) => ({
        lat: item.latitude,
        lng: item.longitude,
        status: item.harvest_status,
        color: STATUS_COLORS[item.harvest_status] || STATUS_COLORS.not_producing,
        data: item,
      }));
  }, [data]);

  // Cascade order: Status -> Country -> Region. statusOptions is the leftmost
  // dropdown so it is NEVER filtered upstream. We preserve the canonical
  // status order (producing -> not_producing -> starting_soon -> ending_soon)
  // used elsewhere in the app for cross-view consistency, only including
  // statuses actually present in the data.
  const statusOptions = useMemo(() => {
    const present = new Set(markers.map((m) => m.status).filter(Boolean));
    return CANONICAL_STATUS_OPTIONS.map((s) => s.id).filter((id) => present.has(id));
  }, [markers]);

  const countryOptions = useMemo(
    () => buildCountryOptions(markers, selectedStatuses),
    [markers, selectedStatuses],
  );

  const regionOptions = useMemo(
    () => buildRegionOptions(markers, selectedStatuses, selectedCountries),
    [markers, selectedStatuses, selectedCountries],
  );

  // Non-expanding prune. The user-driven cascade resets are owned by the
  // modal handlers (Status/Country handlers explicitly setSelectedRegions
  // etc. to "all of new options"), so these effects ONLY remove selections
  // that fall outside the current options. Two reasons that matters:
  //   (1) data refetch may drop a previously-valid country/region/status —
  //       we trim it so the trigger button doesn't say "N selected" while
  //       the map can no longer render it;
  //   (2) hydrate of a saved snapshot pre-cascade might let stale items
  //       slip through — same trim cleans them up on the next render.
  // Identity guard returns the same array reference when nothing changed so
  // React skips the state update.
  useEffect(() => {
    setSelectedStatuses((prev) => {
      const next = prev.filter((s) => statusOptions.includes(s));
      return next.length === prev.length ? prev : next;
    });
  }, [statusOptions]);

  useEffect(() => {
    setSelectedCountries((prev) => {
      const next = prev.filter((c) => countryOptions.includes(c));
      return next.length === prev.length ? prev : next;
    });
  }, [countryOptions]);

  useEffect(() => {
    setSelectedRegions((prev) => {
      const next = prev.filter((r) => regionOptions.includes(r));
      return next.length === prev.length ? prev : next;
    });
  }, [regionOptions]);

  const hydrateProducingRegions = useRef(false);
  const defaultsAppliedRef = useRef(false);

  // GA4 virtual page_view after producing_regions_chart succeeds (withChartData
  // only mounts this component once the query is no longer in initial loading).
  // Once per session so refetches / filter changes do not inflate Views.
  useEffect(() => {
    if (!commodity) return;
    trackGlobalProduceMapViewOncePerSession(commodity);
  }, [commodity]);

  // useLayoutEffect (registered BEFORE usePersistWidgetSnapshot is called
  // below) so this runs first in the commit phase. We clear state and the
  // bookkeeping refs before the persistence hook's hydrate layout effect
  // runs — if a snapshot exists for the new commodity, the hook's setStates
  // (queued AFTER ours in the same commit) will overwrite the empty arrays
  // with the saved values. If no snapshot exists, state stays empty and
  // the defaults useEffect populates "all selected" downstream.
  useLayoutEffect(() => {
    hydrateProducingRegions.current = false;
    defaultsAppliedRef.current = false;
    setSelectedStatuses([]);
    setSelectedCountries([]);
    setSelectedRegions([]);
  }, [commodity]);

  // First-visit defaults: when no Firestore snapshot exists for this scope
  // (so `hydrateMapFilters` is never called), populate all three arrays to
  // their full options once markers load. This is what makes the chart land
  // on "all selected" the first time a user opens it for a given commodity.
  // If a snapshot DID hydrate (hydrateProducingRegions.current === true) we
  // respect whatever it set, including an explicit Clear All ([]).
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    if (markers.length === 0) return;
    if (hydrateProducingRegions.current) {
      defaultsAppliedRef.current = true;
      return;
    }
    if (
      selectedStatuses.length === 0 &&
      selectedCountries.length === 0 &&
      selectedRegions.length === 0
    ) {
      setSelectedStatuses(statusOptions);
      setSelectedCountries(countryOptions);
      setSelectedRegions(regionOptions);
      defaultsAppliedRef.current = true;
    }
  }, [
    markers.length,
    statusOptions,
    countryOptions,
    regionOptions,
    selectedStatuses.length,
    selectedCountries.length,
    selectedRegions.length,
  ]);

  // One-time migration: snapshots saved by the earlier single-select
  // implementation use string fields with an `'all'` sentinel
  // (e.g. `{ selectedStatus: 'producing', selectedCountry: 'all', ... }`).
  // Under the new strict semantics, `'all'` must lift to the full options
  // list (not an empty array) so users continue to see all markers in
  // dimensions they hadn't narrowed. A specific value lifts to a single-
  // element array. Migration is one-way and self-completes on the next save.
  const migrateLegacySnapshot = (s) => {
    if (!s || typeof s !== 'object') return null;
    const hasNewShape =
      Array.isArray(s.selectedStatuses) ||
      Array.isArray(s.selectedCountries) ||
      Array.isArray(s.selectedRegions);
    if (hasNewShape) return null;
    const lift = (v, allOptions) => {
      if (typeof v !== 'string' || !v) return [];
      if (v === 'all') return [...allOptions];
      return [v];
    };
    return {
      selectedStatuses: lift(s.selectedStatus, statusOptions),
      selectedCountries: lift(s.selectedCountry, countryOptions),
      selectedRegions: lift(s.selectedRegion, regionOptions),
    };
  };

  const hydrateMapFilters = useMemo(() => {
    return (raw) => {
      hydrateProducingRegions.current = true;
      // Accept either the new array shape or migrate from the legacy single-
      // select shape. After this point, `s` always has array fields.
      const s = migrateLegacySnapshot(raw) ?? raw ?? {};
      // First-pass validation against the options snapshot for this render.
      // countryOptions / regionOptions narrow under selectedStatuses /
      // selectedCountries — both are still empty on the very first hydrate
      // render, so a persisted downstream value can briefly slip through if
      // it is stale relative to its upstream. The reconciliation useEffects
      // above act as the second pass and trim invalid values on the next
      // commit.
      const nextStatuses = Array.isArray(s.selectedStatuses)
        ? s.selectedStatuses.filter((v) => statusOptions.includes(v))
        : [];
      const nextCountries = Array.isArray(s.selectedCountries)
        ? s.selectedCountries.filter((v) => countryOptions.includes(v))
        : [];
      const nextRegions = Array.isArray(s.selectedRegions)
        ? s.selectedRegions.filter((v) => regionOptions.includes(v))
        : [];
      setSelectedStatuses(nextStatuses);
      setSelectedCountries(nextCountries);
      setSelectedRegions(nextRegions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusOptions, countryOptions, regionOptions]);

  const snapshotMapFilters = useMemo(() => {
    return () => ({
      selectedStatuses,
      selectedCountries,
      selectedRegions,
    });
  }, [selectedStatuses, selectedCountries, selectedRegions]);

  usePersistWidgetSnapshot(
    'producing-regions',
    commodity,
    !!(data && data.length > 0),
    hydrateMapFilters,
    snapshotMapFilters,
    [selectedStatuses, selectedCountries, selectedRegions]
  );

  // Strict multi-select semantics: a marker must match an entry in EVERY
  // selected dimension. An empty selection on any dimension means "no marker
  // passes for that dimension" -> empty result. This is what makes Clear All
  // visibly clear the map.
  const filteredMarkers = useMemo(() => {
    return markers.filter((m) => {
      if (!selectedStatuses.includes(m.status)) return false;
      if (!selectedCountries.includes(String(m.data?.country || ''))) return false;
      if (!selectedRegions.includes(String(m.data?.region || ''))) return false;
      return true;
    });
  }, [markers, selectedStatuses, selectedCountries, selectedRegions]);

  // Country/Region zoom: fit to filtered markers whenever the user has any
  // country or region selected; otherwise reset to global view. When markers
  // are filtered to nothing (empty result) we keep the current viewport so the
  // user isn't yanked around to the global view mid-narrowing.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (selectedCountries.length === 0 && selectedRegions.length === 0) {
      map.setView([20, 0], 2, { animate: true });
      return;
    }
    const pts = filteredMarkers.map((m) => [m.lat, m.lng]);
    if (pts.length === 0) return;
    const lats = pts.map((p) => p[0]);
    const lngs = pts.map((p) => p[1]);
    const sw = [Math.min(...lats), Math.min(...lngs)];
    const ne = [Math.max(...lats), Math.max(...lngs)];
    map.fitBounds([sw, ne], { padding: [24, 24], animate: true });
  }, [selectedCountries, selectedRegions, filteredMarkers]);

  // Count by status for legend
  const statusCounts = useMemo(() => {
    const counts = { producing: 0, not_producing: 0, starting_soon: 0, ending_soon: 0 };
    markers.forEach(m => { counts[m.status] = (counts[m.status] || 0) + 1; });
    return counts;
  }, [markers]);

  const helpDescription = "Interactive map showing active producing regions. Circle size indicates relative volume - larger circles mean higher supply from that region.";

  const pinIconCache = useMemo(() => new Map(), []);
  const getPinIcon = useMemo(() => {
    return (fillColor, status) => {
      const variant = status === 'not_producing' ? 'glass' : 'solid';
      const key = `pin|${fillColor}|${variant}`;
      const cached = pinIconCache.get(key);
      if (cached) return cached;
      const icon = createLeafletPinIcon({ fillColor, sizePx: 26, variant });
      pinIconCache.set(key, icon);
      return icon;
    };
  }, [pinIconCache]);

  // The preset renderer supplies `presetDescription`; the widget dashboard does not.
  const isPresetView = !!presetDescription;

  return (
    <div
      className={
        'h-full flex flex-col bg-transparent' +
        // Single-chart fullscreen: below lg the overlay scrolls, so the card
        // grows with its content and the map keeps a real box below.
        (presetSingleChartFullscreen ? ' preset-fullscreen-fill' : '')
      }
    >
      {!hideTitle ? (
        <div className="px-4">
        <div className="border-b border-[#5D625F] py-2 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1 min-w-0">
              <h3 className="text-[#5D625F] uppercase font-bold tracking-wide" style={{ fontSize: 20 }}>
                {t('charts.producingRegions.title')}
              </h3>
              {!hideHelpButton ? <ChartHelpButton title="Producing Regions" description={helpDescription} /> : null}
            </div>
            {presetSingleChartFullscreen && analyticsFullscreen?.onExit ? (
              <AnalyticsChartFullscreenButton
                isActive
                onEnter={() => {}}
                onExit={analyticsFullscreen.onExit}
              />
            ) : null}
          </div>
        </div></div>
      ) : presetSingleChartFullscreen && analyticsFullscreen?.onExit ? (
        <div className="flex shrink-0 justify-end border-b border-[#5D625F] px-4 py-2">
          <AnalyticsChartFullscreenButton
            isActive
            onEnter={() => {}}
            onExit={analyticsFullscreen.onExit}
          />
        </div>
      ) : null}

      {/* Map Container — isolate + z-0 contains Leaflet’s high z-index (controls ~1000) so the map
          stays under Analytics sticky header (z-50) without lowering zoom below tile panes (~400). */}
      <div
        className={
          'relative z-0 isolate flex min-h-0 flex-1 flex-col' +
          // Below lg in fullscreen the column no longer fills the viewport, so
          // the filters + map box takes the same fixed height the preset rail
          // gives it (`preset-chart-plot-map`).
          (presetSingleChartFullscreen ? ' preset-fullscreen-plot preset-fullscreen-plot-map' : '')
        }
      >
        {/* Filters (multi-select, match View 2 selector style — Select All /
            Clear All toolbar inside the modal). Strict semantics: empty
            selection = no markers visible for that dimension; the trigger
            shows "None" so the visual matches the empty map. "All" appears
            only when every option is selected. */}
        <div className={`px-4 ${isPresetView ? PRESET_FILTER_ROW_Y : 'pt-3 pb-2'} flex-shrink-0`}>
          {/* Below lg the chips collapse behind this toggle; from lg up the strip
              is always open. The Fullscreen button stays in the strip at every
              width (chipsClassName styles only the chips), since this map has
              no title-adjacent fullscreen control to fall back on. */}
          {isPresetView ? (
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="w-full flex items-center justify-between py-2 border-b flex-shrink-0 lg:hidden"
              style={{ borderColor: theme.borderSecondary }}
            >
              <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('filters.filters')}</span>
              {filtersExpanded ? <ChevronUp size={16} style={{ color: theme.secondaryText }} /> : <ChevronDown size={16} style={{ color: theme.secondaryText }} />}
            </button>
          ) : null}

          <PresetFilterStrip
            forceFiltersOpen={isPresetView}
            presetSingleChartFullscreen={presetSingleChartFullscreen}
            analyticsFullscreen={analyticsFullscreen}
            chipsClassName={isPresetView ? (filtersExpanded ? PRESET_FILTER_OPEN_PT : 'max-lg:hidden') : ''}
            gridClassName="grid grid-cols-1 sm:grid-cols-3 gap-3"
          >
            <FilterDropdown
              label={t('filters.status', 'Status')}
              options={statusOptions}
              selected={selectedStatuses}
              allLabel={t('filters.all', 'All')}
              placeholder={t('filters.none', 'None')}
              getOptionLabel={(opt) => STATUS_LABELS[opt] || String(opt)}
              onClick={() => setStatusModalOpen(true)}
              fontSize={fontSize}
              compact={isPresetView}
            />
            <FilterDropdown
              label={t('filters.country', 'Country')}
              options={countryOptions}
              selected={selectedCountries}
              allLabel={t('filters.all', 'All')}
              placeholder={t('filters.none', 'None')}
              onClick={() => setCountryModalOpen(true)}
              fontSize={fontSize}
              compact={isPresetView}
            />
            <FilterDropdown
              label={t('filters.region', 'Region')}
              options={regionOptions}
              selected={selectedRegions}
              allLabel={t('filters.all', 'All')}
              placeholder={t('filters.none', 'None')}
              onClick={() => setRegionModalOpen(true)}
              fontSize={fontSize}
              compact={isPresetView}
            />
          </PresetFilterStrip>
        </div>

        {markers.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[#828382] dark:text-gray-500">
            {t('charts.producingRegions.noData')}
          </div>
        ) : (
          <MapContainer
            className="min-h-0 flex-1"
            center={[20, 0]}
            zoom={2}
            style={{ height: '100%', width: '100%', minHeight: 0, background: isDark ? '#1d1d1d' : '#f3f4f6' }}
            zoomControl={true}
            // Wheel-zoom disabled so page scroll doesn't get hijacked when the
            // cursor passes over the map — zoom is +/− buttons only.
            scrollWheelZoom={false}
            key={isDark ? 'dark' : 'light'}
            whenCreated={(map) => {
              mapRef.current = map;
            }}
          >
            <MapResizeHandler />
            <TileLayer
              url={isDark ? CARTO_DARK_URL : CARTO_LIGHT_URL}
              attribution='&copy; <a href="https://carto.com/">CARTO</a>'
            />

            {filteredMarkers.map((marker, idx) => (
              <Marker
                key={`${marker.data.specific_location || marker.data.region}-${idx}`}
                position={[marker.lat, marker.lng]}
                icon={getPinIcon(marker.color, marker.status)}
                eventHandlers={{
                  // Handle touch events for iPad/mobile devices
                  // This ensures popups open on touch, not just click
                  touchend: (e) => {
                    const layer = e.target;
                    const touch = e.originalEvent.changedTouches?.[0];
                    
                    if (touch && layer._popup) {
                      // Get the touch start position from the layer
                      const startPos = layer._touchStartPos;
                      let shouldOpenPopup = false;
                      
                      if (startPos) {
                        const deltaX = Math.abs(touch.clientX - startPos.x);
                        const deltaY = Math.abs(touch.clientY - startPos.y);
                        // If touch moved less than 10px, consider it a tap (not a drag/pan)
                        if (deltaX < 10 && deltaY < 10) {
                          shouldOpenPopup = true;
                        }
                      } else {
                        // Fallback: if no start position recorded, assume it's a tap
                        // This handles edge cases where touchstart might not have fired
                        shouldOpenPopup = true;
                      }
                      
                      if (shouldOpenPopup) {
                        // Prevent default to avoid triggering click event and map pan
                        e.originalEvent.preventDefault();
                        e.originalEvent.stopPropagation();
                        // Open the popup
                        layer.openPopup();
                      }
                      
                      // Clear the touch start position
                      delete layer._touchStartPos;
                    }
                  },
                  touchstart: (e) => {
                    // Record touch start position to detect taps vs drags
                    const touch = e.originalEvent.touches?.[0];
                    if (touch) {
                      e.target._touchStartPos = {
                        x: touch.clientX,
                        y: touch.clientY,
                      };
                    }
                  },
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <div className={`font-semibold text-base mb-2 ${isDark ? 'text-white' : 'text-[#282A29]'}`}>
                      {toInitCap(marker.data.specific_location || marker.data.region)}
                    </div>
                    <div className="text-sm space-y-1">
                      {marker.data.commodity && (
                        <div>
                          <span className={isDark ? 'text-gray-400' : 'text-[#828382]'}>Commodity:</span>{' '}
                          <span className={isDark ? 'text-white' : 'text-[#282A29]'}>
                            {toInitCap(marker.data.commodity)}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className={isDark ? 'text-gray-400' : 'text-[#828382]'}>Region:</span>{' '}
                        <span className={isDark ? 'text-white' : 'text-[#282A29]'}>
                          {toInitCap(marker.data.region)}, {toInitCap(marker.data.country)}
                        </span>
                      </div>
                      <div>
                        <span className={isDark ? 'text-gray-400' : 'text-[#828382]'}>Status:</span>
                        <span style={{ color: marker.status === 'not_producing' ? 'var(--harvest-not-producing-text, #6B7280)' : marker.color, marginLeft: 4 }}>
                          {STATUS_LABELS[marker.status]}
                        </span>
                      </div>
                      {marker.data.harvest_window &&
                        String(marker.data.harvest_window).replace(/n\/a/gi, '').replace(/[\s,\-]/g, '') !== '' && (
                      <div>
                        <span className={isDark ? 'text-gray-400' : 'text-[#828382]'}>Harvest:</span>{' '}
                        <span className={isDark ? 'text-white' : 'text-[#282A29]'}>
                          {marker.data.harvest_window}
                        </span>
                      </div>
                      )}
                      {marker.data.predominant_varieties &&
                        String(marker.data.predominant_varieties).trim().toLowerCase() !== 'n/a' && (
                        <div>
                          <span className={isDark ? 'text-gray-400' : 'text-[#828382]'}>Varieties:</span>{' '}
                          <span className={isDark ? 'text-white' : 'text-[#282A29]'}>
                            {marker.data.predominant_varieties}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>

      {/* Modals — multi-select via the shared FilterModal. Each handler
          computes downstream resets directly so any change to an upstream
          dimension defaults the lower ones to all of their new options
          (the user's mental model: pick a Country and Region should default
          to all of that country's regions). The non-expanding prune
          effects above only kick in for external option changes (data
          refetch / hydrate drift), never for user actions. */}
      <FilterModal
        isOpen={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        title={t('filters.status', 'Status')}
        options={statusOptions}
        selectedValues={selectedStatuses}
        multiSelect
        onSelect={(v) => {
          const nextStatuses = selectedStatuses.includes(v)
            ? selectedStatuses.filter((x) => x !== v)
            : [...selectedStatuses, v];
          // Cascade rule: when upstream becomes empty, downstream is empty
          // too (nothing to drill into). When upstream is non-empty,
          // downstream defaults to all of its newly-narrowed options.
          if (nextStatuses.length === 0) {
            setSelectedStatuses([]);
            setSelectedCountries([]);
            setSelectedRegions([]);
            return;
          }
          const nextCountries = buildCountryOptions(markers, nextStatuses);
          const nextRegions = buildRegionOptions(markers, nextStatuses, nextCountries);
          setSelectedStatuses(nextStatuses);
          setSelectedCountries(nextCountries);
          setSelectedRegions(nextRegions);
        }}
        onSelectAll={() => {
          const nextStatuses = statusOptions;
          const nextCountries = buildCountryOptions(markers, nextStatuses);
          const nextRegions = buildRegionOptions(markers, nextStatuses, nextCountries);
          setSelectedStatuses(nextStatuses);
          setSelectedCountries(nextCountries);
          setSelectedRegions(nextRegions);
        }}
        onClearAll={() => {
          // All three empty: strict semantics make the map empty, and the
          // downstream triggers correctly read "None".
          setSelectedStatuses([]);
          setSelectedCountries([]);
          setSelectedRegions([]);
        }}
        displayFormatter={(opt) => STATUS_LABELS[opt] || String(opt)}
      />
      <FilterModal
        isOpen={countryModalOpen}
        onClose={() => setCountryModalOpen(false)}
        title={t('filters.country', 'Country')}
        options={countryOptions}
        selectedValues={selectedCountries}
        multiSelect
        onSelect={(v) => {
          const nextCountries = selectedCountries.includes(v)
            ? selectedCountries.filter((x) => x !== v)
            : [...selectedCountries, v];
          // Cascade rule: empty upstream -> empty downstream.
          if (nextCountries.length === 0) {
            setSelectedCountries([]);
            setSelectedRegions([]);
            return;
          }
          const nextRegions = buildRegionOptions(markers, selectedStatuses, nextCountries);
          setSelectedCountries(nextCountries);
          setSelectedRegions(nextRegions);
        }}
        onSelectAll={() => {
          const nextStatuses = selectedStatuses.length > 0 ? selectedStatuses : statusOptions;
          const nextCountries = countryOptions;
          const nextRegions = buildRegionOptions(markers, nextStatuses, nextCountries);
          setSelectedStatuses(nextStatuses);
          setSelectedCountries(nextCountries);
          setSelectedRegions(nextRegions);
        }}
        onClearAll={() => {
          // Country cleared -> Region also empty (no countries to drill into).
          setSelectedCountries([]);
          setSelectedRegions([]);
        }}
      />
      <FilterModal
        isOpen={regionModalOpen}
        onClose={() => setRegionModalOpen(false)}
        title={t('filters.region', 'Region')}
        options={regionOptions}
        selectedValues={selectedRegions}
        multiSelect
        onSelect={(v) =>
          setSelectedRegions((prev) =>
            prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
          )
        }
        onSelectAll={() => {
          const nextStatuses = selectedStatuses.length > 0 ? selectedStatuses : statusOptions;
          const nextCountries =
            selectedCountries.length > 0 ? selectedCountries : buildCountryOptions(markers, nextStatuses);
          const nextRegions = buildRegionOptions(markers, nextStatuses, nextCountries);
          setSelectedStatuses(nextStatuses);
          setSelectedCountries(nextCountries);
          setSelectedRegions(nextRegions);
        }}
        onClearAll={() => setSelectedRegions([])}
      />

      {/* Legend */}
      <div className={`flex flex-wrap gap-4 px-4 ${isPresetView && !presetSingleChartFullscreen ? 'pt-2' : 'py-2'} border-t border-[#E8EBE9] dark:border-[#3a3c3b] flex-shrink-0`}>
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <div key={status} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: color, opacity: status === 'not_producing' ? 0.55 : 1 }}
            />
            <span className="text-xs text-[#828382] dark:text-gray-400">
              {STATUS_LABELS[status]} ({statusCounts[status] || 0})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProducingRegionsMap;
